/* ═══════════ Booking-lifecycle WhatsApp messages ═══════════
   The admin catalogue (/admin/whatsapp › Message templates) was seeded with a message for every
   step of a booking, but for most steps no flow ever sent one (QA 2026-11-04). This file is what
   the steps send: the template CODE each one uses, and the seeded wording as the fallback body for
   a database whose row is missing or switched off (same contract as every other send —
   `waTemplateMessage`).

   Payment confirmations from Curlec are applied on the SERVER (PaymentService.MarkPaidFromGatewayAsync)
   and send their own copy of the four payment messages from there — keep those defaults in step
   with the C# `BookingWhatsAppNotifier`. */
import type { WhatsAppTemplateSdk } from '@libs/graphql/sdk/whats-app-template/whats-app-template.sdk';
import { renderWaTemplate, waTemplateMessage } from './wa-template.util';
import { fmtRM } from './money';

export const WA_LIFECYCLE = {
  /** → customer: the vendor sent the invoice. */
  invoiceSent: 'cust_invoice_sent',
  /** → customer: deposit received (vendor-recorded here; Curlec ones from the API). */
  depositPaidToCustomer: 'cust_deposit_paid',
  /** → customer: the vendor marked the event complete — please confirm delivery. */
  completionRequest: 'cust_completion_request',
  /** → customer: the vendor cancelled the booking. */
  cancelledToCustomer: 'cust_booking_cancelled',
  /** → vendor: the customer confirmed the event was delivered. */
  completionConfirmed: 'completion_confirmed',
  /** → vendor: the customer reported a problem with the event. */
  completionDisputed: 'completion_disputed',
  /** → vendor: the customer cancelled the booking. */
  cancelledToVendor: 'booking_cancelled',
  /** → reseller: every partner on the booking has now approved. */
  partnerAgreed: 'partner_agreed',
  /** → reseller: a partner declined their part. */
  partnerDeclined: 'partner_declined',
  /** → partner: the reseller proposed replacing their service. */
  portionReplaced: 'portion_replaced',
  /** → partner: their service was taken off the order. */
  portionRemoved: 'portion_removed',
  /** → incoming partner: the customer accepted them as the replacement. */
  subConfirmed: 'sub_confirmed',
  /** → partner: the customer kept their service after a removal was proposed. */
  subKept: 'sub_kept',
  /** → reseller: the customer finished choosing their menu. */
  menuSelected: 'menu_selected',
  /** → vendor (own copy): they marked the event complete. */
  completionRequested: 'completion_requested',
  /** → reseller: Trexa resolved the customer's reported problem. */
  disputeResolved: 'dispute_resolved',
  /** → reviewed vendor: the customer left a review. */
  reviewReceived: 'review_received',
  /** → vendor (own copy): their early-release request was sent. */
  earlyRequested: 'early_requested',
  /** → vendor: Trexa paid their early release. */
  earlyReleased: 'early_released',
  /** → vendor: Trexa declined their early-release request. */
  earlyRejected: 'early_rejected',
  /** → vendor: their pending request closed because the booking completed / was cancelled. */
  earlyVoided: 'early_voided',
} as const;
export type WaLifecycleCode = typeof WA_LIFECYCLE[keyof typeof WA_LIFECYCLE];

/** Label + body each template was seeded with — the fallback when its row is missing/disabled. */
const DEFAULTS: Record<WaLifecycleCode, { label: string; body: string }> = {
  cust_invoice_sent: {
    label: 'Invoice sent',
    body: 'Hi {customerName} 🙂 The invoice for your event at {hallName} on {eventDate} is ready — total {amount}. Log in to Trexa to review and pay the deposit',
  },
  cust_deposit_paid: {
    label: 'Deposit confirmation',
    body: 'Thank you {customerName}! Your deposit of {amount} for the {eventDate} event at {hallName} is safely in Trexa escrow. Your date is now locked in ✅',
  },
  cust_completion_request: {
    label: 'Confirm event delivered',
    body: 'Hi {customerName}, congratulations on your {eventDate} event! 🎊 Please confirm in Trexa that {hallName}’s service was delivered so the final balance can be released.',
  },
  cust_booking_cancelled: {
    label: 'Cancellation notice',
    body: 'Hi {customerName}, your booking at {hallName} for {eventDate} has been cancelled.{refundNote}',
  },
  completion_confirmed: {
    label: 'Customer confirmed the event',
    body: '🎉 {customerName} confirmed the {eventDate} event was delivered. The final balance has been released from escrow to your account.',
  },
  completion_disputed: {
    label: 'Customer reported a problem',
    body: '⚠️ {customerName} reported an issue with the {eventDate} event. The balance stays in escrow until this is resolved.',
  },
  booking_cancelled: {
    label: 'Booking cancelled',
    body: 'The {eventDate} booking ({customerName}) has been cancelled. Please check Trexa for the refund/forfeit details.',
  },
  partner_agreed: {
    label: 'Partner approved the slot',
    body: 'Your partner has approved the slot for the {eventDate} booking. All services are ready — you can go ahead and send the invoice.',
  },
  partner_declined: {
    label: 'Partner declined the slot',
    body: '⚠️ A partner can’t take the slot for the {eventDate} booking. Please find a replacement partner or remove that service in Trexa.',
  },
  portion_replaced: {
    label: 'Service proposed for replacement',
    body: '{hallName} proposed replacing your {serviceName} on the {eventDate} booking with another partner. Waiting on the customer’s decision.',
  },
  portion_removed: {
    label: 'Service removed',
    body: 'Your {serviceName} on the {eventDate} booking ({hallName}) has been removed from the order.',
  },
  sub_confirmed: {
    label: 'Customer confirmed the replacement',
    body: '🎉 The customer confirmed you as the replacement for {serviceName} on the {eventDate} booking. Please check the details in Trexa.',
  },
  sub_kept: {
    label: 'Customer kept the original service',
    body: 'The customer chose to keep the original {serviceName} for the {eventDate} booking. No change to the order.',
  },
  menu_selected: {
    label: 'Menu selected by customer',
    body: '🍽️ {customerName} has completed their menu selection for the {eventDate} booking. Please check it in Trexa.',
  },
  completion_requested: {
    label: 'Awaiting customer confirmation',
    body: 'The {eventDate} event has been marked complete. Waiting for {customerName} to confirm before the final balance releases from escrow.',
  },
  dispute_resolved: {
    label: 'Dispute resolved',
    body: 'The dispute for the {eventDate} event has been resolved. Please check Trexa for the release/refund details.',
  },
  review_received: {
    label: 'New review received',
    body: '⭐ {customerName} left a review for the {eventDate} event. Please check it and reply if you’d like, on your profile.',
  },
  early_requested: {
    label: 'Early-release request sent',
    body: 'Your early-release request of {amount} for the {eventDate} booking has been sent. Trexa will review and process it.',
  },
  early_released: {
    label: 'Early release paid out',
    body: '💸 Your early release of {amount} for the {eventDate} booking has been paid out to your account. This amount will be netted off at final settlement.',
  },
  early_rejected: {
    label: 'Early-release request not approved',
    body: 'Your early-release request for the {eventDate} booking wasn’t approved this time. The full payout still releases once the event is completed.',
  },
  early_voided: {
    label: 'Early-release request closed',
    body: 'The {eventDate} booking completed or was cancelled before your early-release request was processed. The request is closed, with no effect on your final payout.',
  },
};

/** The `{token}`s a lifecycle template may use. Pass what the step knows; unknown ones stay blank. */
export interface LifecycleMsgContext {
  customerName?: string;
  vendorName?: string;
  /** The vendor as the CUSTOMER catalogue calls it ({hallName}) — the booking's reseller. */
  hallName?: string;
  eventDateLong?: string;
  bookingRef?: string;
  /** Already-formatted money, e.g. "RM 9,000.00". */
  amountLabel?: string;
  /** Cancellation refund (number) — becomes `{refundNote}`, a whole sentence or nothing. */
  refund?: number;
  /** The partner the step is about ({partnerName}). */
  partnerName?: string;
  /** The service the step is about ({serviceName}). */
  serviceName?: string;
}

export function lifecycleVars(c: LifecycleMsgContext): Record<string, string> {
  return {
    customerName: c.customerName ?? '',
    vendorName: c.vendorName ?? '',
    hallName: c.hallName ?? c.vendorName ?? '',
    eventDate: c.eventDateLong ?? '',
    bookingRef: c.bookingRef ?? '',
    amount: c.amountLabel ?? '',
    partnerName: c.partnerName ?? '',
    serviceName: c.serviceName ?? '',
    refundNote: (c.refund ?? 0) > 0
      ? ` A refund of ${fmtRM(c.refund ?? 0)} will be processed back to your original payment method.`
      : '',
  };
}

/** Body + History label for a lifecycle step: the admin template, else the seeded wording. */
export function lifecycleMessage(
  sdk: WhatsAppTemplateSdk, code: WaLifecycleCode, c: LifecycleMsgContext,
): Promise<{ body: string; label: string }> {
  const vars = lifecycleVars(c);
  const d = DEFAULTS[code];
  return waTemplateMessage(sdk, code, vars, renderWaTemplate(d.body, vars), d.label);
}
